import { FleetTracingArea } from '@backoffice/fleet-tracing'
import { InAppMessagesArea } from '@backoffice/in-app-messages'
import { TripApiConfigurationArea } from '@backoffice/trip-api-configuration'
import { UserManagementOrganizationArea } from '@backoffice/user-management-organization'
import { VehicleCapacityConfigArea } from '@backoffice/vehicle-capacity-config'
import { AccidentsReportsArea } from '@backoffice-frontend/accidents-reports'
import { AccountingArea } from '@backoffice-frontend/accounting'
import { AdConfigurationArea } from '@backoffice-frontend/ad-configuration'
import { AdFleetMapArea } from '@backoffice-frontend/ad-fleet-map'
import { AlertsArea } from '@backoffice-frontend/alerts'
import { AlertsTestingArea } from '@backoffice-frontend/alerts-testing'
import { AppAccessControlArea } from '@backoffice-frontend/app-access-control'
import type { SectionsConfig } from '@backoffice-frontend/authenticated-app-view'
import { CapacityPlanningArea } from '@backoffice-frontend/capacity-planning'
import { CouponArea } from '@backoffice-frontend/coupon'
import { CustomerArea } from '@backoffice-frontend/customer'
import { CustomerFeatureAllowlistingArea } from '@backoffice-frontend/customer-feature-allowlisting'
import { DemoRoutesArea } from '@backoffice-frontend/demo-routes'
import { DisruptionsArea } from '@backoffice-frontend/disruptions'
import { DriverTippingArea } from '@backoffice-frontend/driver-tipping'
import { DsBossArea } from '@backoffice-frontend/ds-boss'
import { EmployeeArea } from '@backoffice-frontend/employee'
import { EmployeeTimeManagementArea } from '@backoffice-frontend/employee-time-management'
import { isProduction } from '@backoffice-frontend/environment'
import { FleetMaintenanceArea } from '@backoffice-frontend/fleet-maintenance'
import { FleetManagementTestingArea } from '@backoffice-frontend/fleet-management-testing'
import { FleetMapArea } from '@backoffice-frontend/fleet-map'
import { FleetPerformanceOverviewArea } from '@backoffice-frontend/fleet-performance-overview'
import { FleetsArea } from '@backoffice-frontend/fleets'
import { UserType } from '@backoffice-frontend/graphql'
import { KpiDashboardArea } from '@backoffice-frontend/kpi-dashboard'
import { LegalDocumentManagementArea } from '@backoffice-frontend/legal-document-management'
import { MobileAppFlavorConfigArea } from '@backoffice-frontend/mobile-app-flavor-config'
import { MobileAppstoreAssetsArea } from '@backoffice-frontend/mobile-appstore-assets'
import { OfferAndPoolingArea } from '@backoffice-frontend/offer-and-pooling'
import { PassengerAppArea } from '@backoffice-frontend/passenger-app'
import { PatternLibraryArea } from '@backoffice-frontend/pattern-library'
import { PricingArea } from '@backoffice-frontend/pricing'
import { ReportIoArea } from '@backoffice-frontend/report-io'
import { useUserHasAccess } from '@backoffice-frontend/restricted'
import { RiderFeedbackArea } from '@backoffice-frontend/rider-feedback'
import { RoadNetworkArea } from '@backoffice-frontend/road-network'
import { SafetyDriverGuidanceArea } from '@backoffice-frontend/safety-driver-guidance'
import { ScLabsArea } from '@backoffice-frontend/sc-labs'
import { ServiceBreakLocationsArea } from '@backoffice-frontend/service-break-locations'
import { ServiceSettingsArea } from '@backoffice-frontend/service-settings'
import { ShiftsArea } from '@backoffice-frontend/shifts'
import { ShiftsCreationArea } from '@backoffice-frontend/shifts-creation'
import { StatusArea } from '@backoffice-frontend/status'
import { StopNetworkArea } from '@backoffice-frontend/stop-network'
import { TripArea } from '@backoffice-frontend/trip'
import { UpdatesArea } from '@backoffice-frontend/updates'
import { useBackofficeLoggedInUser } from '@backoffice-frontend/user-management-ui'
import { VehicleArea } from '@backoffice-frontend/vehicle'
import { VehicleAlertsArea } from '@backoffice-frontend/vehicle-alerts'
import { VehicleDeviceManagementArea } from '@backoffice-frontend/vehicle-device-management'
import { VirtualAdVehiclesArea } from '@backoffice-frontend/virtual-ad-vehicles'
import { WelcomeArea } from '@backoffice-frontend/welcome'
import { BoumRight } from '@moia-dev/moia-token-claims'
import { useTranslation } from 'react-i18next'

export const useNavigationSections = (): SectionsConfig => {
  const { user } = useBackofficeLoggedInUser()

  const { t } = useTranslation()

  const hasAccessToApproverFeature = useUserHasAccess({
    allowedRights: [BoumRight.BACKOFFICE_APPROVER_FEATURE],
  })
  const isAdminOrPlatformAdmin =
    user?.userType === UserType.Admin ||
    user?.userType === UserType.PlatformAdmin

  const sectionConfig: SectionsConfig = [
    // Route-only areas (hidden from navigation bar)
    {
      areas: [WelcomeArea, UpdatesArea, StatusArea, ReportIoArea],
      hiddenInNavBar: true,
    },
    // Individual areas without section labels
    {
      areas: [
        KpiDashboardArea,
        FleetPerformanceOverviewArea,
        ScLabsArea,
        AlertsArea,
      ],
    },
    //Sections with labels
    {
      label: t('backofficeApp_staffSection_navigation'),
      areas: [
        EmployeeArea,
        ShiftsArea,
        !isProduction() ? ShiftsCreationArea : null,
        DriverTippingArea,
        EmployeeTimeManagementArea,
        CapacityPlanningArea,
      ],
    },
    {
      label: t('backofficeApp_fleetSection_navigation'),
      areas: [
        VehicleAlertsArea,
        VehicleArea,
        FleetMapArea,
        AdFleetMapArea,
        FleetMaintenanceArea,
        SafetyDriverGuidanceArea,
        AccidentsReportsArea,
        DisruptionsArea,
        FleetsArea,
        FleetTracingArea,
        VehicleDeviceManagementArea,
        DemoRoutesArea,
        VehicleCapacityConfigArea,
        ServiceBreakLocationsArea,
      ],
    },
    {
      label: t('backofficeApp_serviceAreaSection_navigation'),
      areas: [StopNetworkArea],
    },
    {
      label: t('backofficeApp_infrastructureSection_navigation'),
      areas: [RoadNetworkArea],
    },
    {
      label: t('backofficeApp_customersSection_navigation'),
      areas: [
        CustomerArea,
        TripArea,
        TripApiConfigurationArea,
        CustomerFeatureAllowlistingArea,
        MobileAppFlavorConfigArea,
        MobileAppstoreAssetsArea,
        LegalDocumentManagementArea,
        AppAccessControlArea,
        RiderFeedbackArea,
        InAppMessagesArea,
      ],
    },
    {
      label: t('backofficeApp_salesSection_navigation'),
      areas: [CouponArea, AdConfigurationArea, AccountingArea],
    },
    !isProduction()
      ? {
          label: t('backofficeApp_testingSection_navigation'),
          areas: [
            PassengerAppArea,
            AlertsTestingArea,
            PatternLibraryArea,
            FleetManagementTestingArea,
            VirtualAdVehiclesArea,
          ],
        }
      : null,
    {
      label: t('backofficeApp_mobilityIntelligenceSuite_navigation'),
      areas: [
        ServiceSettingsArea,
        OfferAndPoolingArea,
        PricingArea,
        DsBossArea,
      ],
    },
    {
      label: t('backofficeApp_adminSection_navigation'),
      areas: [
        isAdminOrPlatformAdmin || hasAccessToApproverFeature
          ? UserManagementOrganizationArea
          : null,
      ],
    },
  ]

  return sectionConfig
}
